/**
 * answer-edit.js
 */

import React from 'react';
import axios from 'axios';
import {
  IconButton,
} from '@mui/material';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { parseISO } from 'date-fns';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { shouldRenderMobileLayout } from 'utils/app-context';
import useMounted from 'utils/use-mounted';
import useRecordStore from 'contexts/record-store';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import TrainingWizardStepper from 'components/training/training-drawer/training-wizard-stepper';
import Button from 'components/button';
import Icon from 'components/icon';
import RejectionAlert from 'components/rejection-alert';
import OutdatedAlert from '../outdated-alert';
import ReviewInvalidationAlert from 'components/review-invalidation-alert';
import AnswerEditMain from './answer-edit-main';
import AnswerEditDetection from './answer-edit-detection';
import AnswerEditWeather from './answer-edit-weather';
import AnswerEditComments from './answer-edit-comments';
import AnswerEditPatrol from './answer-edit-patrol';
import AnswerEditPhotos from './answer-edit-photos';


// use wrapper to remount component when answer ID changes
export default function AnswerEdit(props) {
  const params = useParams();
  const answerId = params.answerId || null;

  return <AnswerEditImplementation
    answerId={answerId}
    key={answerId}
    {...props} 
  />;
}


function AnswerEditImplementation (props) {
  const {
    answerId,
    setDesktopActions,
    setMobileAppBar,
    drawerState,
    onClose,
    wizard,
    basePath,
  } = props;
  const isMounted = useMounted();
  let history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const recordStore = useRecordStore();
  const params = useParams();
  const translation = 'answerEdit';
  const { t: gt } = useTranslation('general');
  const exerciseId = params.exerciseId || null;
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId);
  const answerRecord = recordStore.useRecord(Types.Record.ANSWER, answerId);
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, answerRecord.get()?.eventId);
  const [storedRecord, setStoredRecord] = React.useState();
  const [options, setOptions] = React.useState();
  const [submittedState, setSubmittedState] = React.useState();
  const [isDirtyForced, setIsDirtyForced] = React.useState(false);
  const dateCompleted = answerRecord.get()?.dateCompleted;
  const eventId = eventRecord.get()?.id;
  const isFutureEvent = parseISO(eventRecord.get()?.date) > new Date();
  const isOptional = eventRecord.get()?.membership?.isOptional;
  const isAttending = eventRecord.get()?.membership?.isAttending;
  const workMode = exerciseRecord.get()?.workMode;
  const workModeName = workMode ? Types.WorkMode.getName(gt, workMode).toLowerCase() : '';
  const isOutdated = answerRecord.get()?.isOutdated;

  // hook form
  const formContext = useForm();
  const { formState, handleSubmit, reset } = formContext;

  // props sent to any sub-form component
  const formProps = {
    storedRecord,
    answerId,
    exerciseId,
    options,
    drawerState,
    translation,
    workMode,
    workModeName
  };


  // get answer options on init
  React.useEffect(() => {
    if (answerRecord.get() === undefined) return;

    setTimeout(() => drawerState.loading.content.set(true));
    (async () => {
      if (parseInt(answerId) >= 0) {
        const response = await axios.get(`/training/answer/${answerId}/edit`);

        if (!isMounted.current) return;

        if (response.status === 200) {
          const newAnswer = JSON.parse(JSON.stringify(answerRecord.get()));
          if (response.data.odorResponses) {
            newAnswer.detection.environments = response.data.odorResponses;
          }
          // Remove detection.environments from form data. With react-hook-form v7 these get stored and 
          // included with submission data even though there is no corresponding form field,
          // causing a submission error. Using shouldUnregister in useForm prevents this
          // but causes other problems.
          let { detection, ...formInitData } = newAnswer;
          if (detection) {
            const {environments: _, ...detectionRest} = detection;
            formInitData = { detection: detectionRest, ...formInitData };
          }
          reset(formInitData);
          setStoredRecord(newAnswer);
          setOptions(response.data.options);
        }
        
        // check if not attending
        if (isOptional && isAttending === null) {
          await recordStore.setAttendance(true, eventId);
        }
      }

      drawerState.loading.content.set(false);
    })();

  // drawerState causes infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerId, eventId, isOptional, isAttending, isMounted]);


  // if outdated, FORCE allow saving without changes
  React.useEffect(() => {
    if (isOutdated === true) {
      setIsDirtyForced(true);
    }
  }, [isOutdated]);


  // redirect to completion view (for better explanation) if event is in the future
  React.useEffect(() => {
    if (isFutureEvent) {
      history.push(makeLocation(
        basePath,
        'training',
        'viewAnswer',
        { exerciseId, answerId },
        history.location
      ));
    }
  }, [isFutureEvent, history, basePath, exerciseId, answerId]);


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data, noNav) => {
    setSubmittedState({ data, noNav });
  }, []);


  const afterSubmit = React.useCallback(async (data, noNav) => {
    drawerState.loading.content.set(true);

    try {
      const { record: savedAnswerRecord } = await recordStore.saveAnswer(answerId, data, dateCompleted);
      if (savedAnswerRecord.get()) {
        if (wizard) {
          if (noNav !== true) {
            wizard.onSave(savedAnswerRecord.get(), history, basePath, recordStore);
          }
        } else {
          enqueueSnackbar(
            `Exercise completed for ${savedAnswerRecord.get().dog.name}`, 
            { variant: 'success' }
          );
          if (noNav !== true) {
            history.push(makeLocation(
              basePath,
              'training',
              'viewAnswer',
              {exerciseId, answerId: savedAnswerRecord.get().id},
              history.location
            ));
          }
        }
      }

    } catch(e) {
      enqueueSnackbar('Error saving answer', {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });

      drawerState.loading.content.set(false);
    }

  // recordStore and drawerState cause infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerId, exerciseId, basePath, dateCompleted, enqueueSnackbar, history, wizard]);


  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data, submittedState.noNav);
    }
    // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const onSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);


  const onSkip = React.useCallback(() => {
    wizard.onSave(storedRecord, history, basePath, recordStore);

  // recordStore causes infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, storedRecord, history, basePath]);


  const onBack = React.useCallback(() => {
    history.push(makeLocation(
      basePath,
      'training',
      'viewExercise',
      {exerciseId},
      history.location
    ));
  }, [history, basePath, exerciseId]);


  const onCancel = React.useCallback(() => {
    history.push(makeLocation(
      basePath,
      'training',
      'viewAnswer',
      {exerciseId, answerId},
      history.location
    ));
  }, [history, basePath, exerciseId, answerId]);


  const mobileBackButton = React.useMemo(() => (
    <IconButton
      onClick={onBack}
      size="small"
      style={{color: 'inherit'}}
    >
      <Icon type='nav-back' />
    </IconButton>
  ), [onBack]);


  const cancelButton = React.useMemo(() => (
    <DrawerActionButton
      onClick={onCancel}
    >
      Cancel
    </DrawerActionButton>
  ), [onCancel]);


  const saveButton = React.useMemo(() => (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty && !wizard && !isDirtyForced}
      onClick={onSave}
      style={{ marginLeft: 16 }}
      data-testid="save-button"
    >
      {wizard ? 'Next' : 'Save'}
    </DrawerActionButton>
  ), [wizard, onSave, formState.isDirty, isDirtyForced]);


  const skipButton = React.useMemo(() => (
    <DrawerActionButton
      onClick={onSkip}
    >
      Skip
    </DrawerActionButton>
  ), [onSkip]);


  const closeButton = React.useMemo(() => (
    <IconButton
      data-testid="mobile-drawer-close-button"
      onClick={onClose}
      size="small"
      style={{color: 'inherit'}}
    >
      <Icon type='close' />
    </IconButton>
  ), [onClose]);


  React.useEffect(() => {
    if (setDesktopActions) {
      setDesktopActions(wizard
        ? <>{skipButton}{saveButton}</>
        : <>{cancelButton}{saveButton}</>);
    }
    if (setMobileAppBar) {
      setMobileAppBar({
        title: 'Completion',
        leftActions: storedRecord && storedRecord.dateCompleted
          ? cancelButton
          : Boolean(wizard) ? closeButton : mobileBackButton,
        rightActions: saveButton,
        recordGroupType: Types.RecordGroup.TRAINING,
      })
    }
  }, [
    setDesktopActions,
    setMobileAppBar,
    wizard,
    storedRecord,
    saveButton,
    cancelButton,
    skipButton,
    closeButton,
    mobileBackButton
  ]);


  return (
    <>
      {(storedRecord && options) &&
      <FormProvider {...formContext}>
        <form onSubmit={handleSubmit(onSubmit)}>

          {/* mobile wizard & dog name row */}
          <MobilePageTitle
            title={<>
              {exerciseRecord.get().name}
              <>&nbsp;&nbsp;&middot;&nbsp;&nbsp;</>
              {answerRecord.get().dog.name.toLowerCase().includes('k9')
                ? answerRecord.get().dog.name
                : `K9 ${answerRecord.get().dog.name}`}
            </>}
            rightAction={wizard &&
              <Button
                upperCase
                endIcon={<Icon type='arrow-right' />}
                onClick={onSkip}
                children='skip step'
              />}
          />

          <DrawerContent>

            {shouldRenderMobileLayout() && wizard &&
            <TrainingWizardStepper basePath={basePath} />}

            <RejectionAlert review={answerRecord.get().review} />
            <ReviewInvalidationAlert review={answerRecord.get().review} />
            <OutdatedAlert answer={answerRecord.get()} exercise={exerciseRecord.get()} />

            {!isFutureEvent &&
            <>
              {/* main section */}
              <AnswerEditMain {...formProps} />

              {/* detection */}
              <AnswerEditDetection {...formProps} />

              {/* patrol */}
              <AnswerEditPatrol {...formProps} />

              {/* weather */}
              <AnswerEditWeather {...formProps} />

              {/* comments */}
              <AnswerEditComments {...formProps} />

              {/* photos */}
              <AnswerEditPhotos {...formProps} />

              {/* extra scroll space */}
              <div style={{ height: 120 }} />

            </>}

          </DrawerContent>

        </form>
      </FormProvider>}

      <ConfirmUnsavedEditor
        dirty={formState.isDirty && !formState.isSubmitted}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                onSubmit(data, true);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />
    </>
  );
}